import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GripVertical, ImageIcon, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import type { BrandLogo } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import LogoLoader from '@/components/ui/LogoLoader'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import Badge from '@/components/ui/Badge'
import { useToast } from '@/components/ui/toast'

interface Draft {
  id?: number
  name: string
  image_path: string
  image_key: string | null
  active: boolean
  sort: number
}

const emptyDraft: Draft = {
  name: '',
  image_path: '',
  image_key: null,
  active: true,
  sort: 0,
}

export default function AdminBrandLogos() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<BrandLogo | null>(null)
  // Chosen file is held locally and previewed with an object URL, so nothing
  // is uploaded until Save. A cancelled edit therefore leaves no orphan file.
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)

  const logos = useQuery({
    queryKey: ['admin-brand-logos'],
    queryFn: async () => (await api.get<{ data: BrandLogo[] }>('/admin/brand-logos')).data.data,
  })

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(file)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const invalidate = () => {
    for (const key of ['admin-brand-logos', 'storefront-brands']) {
      queryClient.invalidateQueries({ queryKey: [key] })
    }
  }

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      let imagePath = d.image_path
      let imageKey = d.image_key

      // Upload first so the logo is only written once we have a real path.
      if (file) {
        const form = new FormData()
        form.append('image', file)
        const uploaded = (
          await api.post<{ image_path: string; image_key: string }>('/admin/brand-logos/upload', form)
        ).data
        imagePath = uploaded.image_path
        // Carries the ownership of the new file. Without it the backend cannot
        // tell this upload from a hand-typed path and would never clean it up.
        imageKey = uploaded.image_key
      }

      const payload = {
        name: d.name,
        image_path: imagePath,
        image_key: imageKey,
        active: d.active,
        sort: d.sort,
      }

      return d.id
        ? (await api.patch(`/admin/brand-logos/${d.id}`, payload)).data
        : (await api.post('/admin/brand-logos', payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      invalidate()
      toast.success(res.message ?? 'Saved')
      setDraft(null)
      setFile(null)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/admin/brand-logos/${id}`)).data,
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setConfirmDelete(null)
      toast.success(res.message ?? 'Deleted')
    },
    onError: (err) => {
      setConfirmDelete(null)
      toast.error('Delete failed', errorMessage(err))
    },
  })

  const rows = logos.data ?? []

  const openNew = () => {
    setFile(null)
    setDraft({ ...emptyDraft, sort: rows.length })
  }

  const openEdit = (logo: BrandLogo) => {
    setFile(null)
    setDraft({
      id: logo.id,
      name: logo.name,
      image_path: logo.image_path,
      image_key: logo.image_key ?? null,
      active: logo.active ?? true,
      sort: logo.sort ?? 0,
    })
  }

  if (logos.isLoading) return <LogoLoader />

  const shownImage = preview ?? draft?.image_path

  return (
    <div>
      <PageHeader
        title="Brand logos"
        subtitle="Logos in the storefront's Shop by apparel strip. Lowest sort shows first; only active logos appear."
        actions={
          <Button onClick={openNew}>
            <Plus className="size-4" /> New logo
          </Button>
        }
      />

      <Table columns={['', 'Logo', 'State', actionsColumn]}>
        {rows.length === 0 ? (
          <EmptyRow colSpan={4}>No logos yet. The storefront hides the strip until you add one.</EmptyRow>
        ) : (
          rows.map((logo) => (
            <Row key={logo.id} onClick={() => openEdit(logo)}>
              <Cell className="w-8 text-foreground/40">
                <GripVertical className="size-4" />
              </Cell>
              <Cell>
                <div className="flex items-center gap-3">
                  {/* object-contain, not cover - a cropped logo is a wrong logo. */}
                  <div className="grid h-10 w-16 shrink-0 place-items-center overflow-hidden rounded border border-foreground/10 bg-white p-1">
                    {logo.image_path ? (
                      <img
                        src={logo.image_path}
                        alt=""
                        loading="lazy"
                        className="size-full object-contain"
                        onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                      />
                    ) : (
                      <ImageIcon className="size-4 text-foreground/30" />
                    )}
                  </div>
                  <p className="truncate font-medium">{logo.name}</p>
                </div>
              </Cell>
              <Cell>
                <Badge tone={logo.active ? 'success' : 'neutral'}>{logo.active ? 'Active' : 'Hidden'}</Badge>
              </Cell>
              <Cell className={actionsCellClass}>
                <Button
                  variant="ghost"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation()
                    openEdit(logo)
                  }}
                >
                  <Pencil className="size-4" />
                </Button>
                <button
                  type="button"
                  title={`Delete ${logo.name}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    setConfirmDelete(logo)
                  }}
                  className="-m-2 p-2 text-foreground/50 transition hover:text-danger sm:m-0 sm:ml-2 sm:p-0"
                >
                  <Trash2 className="size-4" />
                </button>
              </Cell>
            </Row>
          ))
        )}
      </Table>

      <Modal
        open={Boolean(draft)}
        onClose={() => {
          setDraft(null)
          setFile(null)
        }}
        title={draft?.id ? 'Edit logo' : 'New logo'}
        width="max-w-2xl"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setDraft(null)
                setFile(null)
              }}
            >
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!draft?.name || (!draft?.image_path && !file)}
              onClick={() => draft && save.mutate(draft)}
            >
              Save logo
            </Button>
          </>
        }
      >
        {draft && (
          <div className="max-h-[65vh] space-y-4 overflow-y-auto pr-1 scrollbar-thin">
            {/* Preview at roughly the size the storefront strip renders. */}
            <div className="grid h-40 place-items-center rounded-xl border border-foreground/10 bg-white">
              {shownImage ? (
                <img src={shownImage} alt="" className="h-24 max-w-[80%] object-contain" />
              ) : (
                <p className="text-sm text-foreground/40">No image chosen</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-lg border border-primary/60 bg-primary/20 px-4 text-sm font-semibold text-primary hover:bg-primary/30">
                <Upload className="size-4" />
                {draft.image_path || file ? 'Replace image' : 'Choose image'}
                <input
                  type="file"
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <p className="text-xs text-foreground/50">PNG with transparency recommended. Max 4 MB.</p>
            </div>

            <Field label="Image path" htmlFor="image_path">
              <Input
                id="image_path"
                value={draft.image_path}
                // Typing a path by hand points the logo at something we did
                // not upload, so it stops owning whatever file it had.
                onChange={(e) => setDraft({ ...draft, image_path: e.target.value, image_key: null })}
                placeholder="/brands/adidas.png or upload above"
              />
            </Field>

            <Field label="Brand name" htmlFor="name">
              <Input
                id="name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Adidas"
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Sort (lowest shows first)" htmlFor="sort">
                <Input
                  id="sort"
                  type="number"
                  min={0}
                  value={draft.sort}
                  onChange={(e) => setDraft({ ...draft, sort: Number(e.target.value) })}
                />
              </Field>
              <Field label="Visibility">
                <label className="inline-flex h-10 cursor-pointer items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.active}
                    onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                    className="size-4 rounded border-foreground/30"
                  />
                  Show this logo on the storefront
                </label>
              </Field>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title="Delete logo?"
        footer={
          <>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete.id)}
            >
              Delete logo
            </Button>
          </>
        }
      >
        <p className="text-sm text-foreground/70">
          "{confirmDelete?.name}" is removed from the strip, and its uploaded image is deleted. This cannot be undone.
        </p>
      </Modal>
    </div>
  )
}
